/**
 * Settings for the "Dispatch Engine" admin tab (nearest-few-first dispatch). node --test
 *
 * The server (firebase/functions/dispatchWaves.js, resolveDispatchV2) reads ONE top-level field of
 * config/platform, `dispatchV2`, in three layers, last one wins:
 *
 *   code defaults  ->  dispatchV2  ->  dispatchV2.towns[zoneId]      (zoneId "default" = Piler)
 *
 * It is a field of its own on purpose: the Settings page's Dispatch tuning box replaces the whole
 * `dispatch` block and the Service zones box rewrites the whole `zones` array when saved, so
 * anything stored in either would be wiped. This tab saves ONLY `dispatchV2`, and the server
 * re-clamps every value, so a mistake here can never break matching.
 *
 * These helpers read and clamp exactly like the server (a test compares them with the server's own
 * module over many inputs) so the preview always shows what the server will really do.
 */

export const STRATEGIES = ['rings', 'batched', 'nearest'];

// Same names, defaults and clamps as WAVE_LIMITS in firebase/functions/dispatchWaves.js.
export const FIELDS = [
  { key: 'batchSize', label: 'Annas called per group', hint: 'The nearest Annas are called a few at a time.', min: 1, max: 10, dflt: 3 },
  { key: 'batchHoldSec', label: 'Seconds before the rest of a crowded circle', hint: 'Circles in batches: the nearest few are called first, the rest this many seconds later.', min: 2, max: 25, dflt: 7 },
  { key: 'waveHoldSec', label: 'Seconds each group gets', hint: 'If nobody accepts, the next group is added after this.', min: 3, max: 30, dflt: 10 },
  { key: 'maxWaves', label: 'Number of groups', hint: 'The last group is everyone still left, called together.', min: 2, max: 12, dflt: 6 },
  { key: 'waveHoldFinalSec', label: 'Seconds the last group gets', hint: 'Before the stale-GPS fallback starts.', min: 5, max: 30, dflt: 15 },
  { key: 'normalPhaseBudgetSec', label: 'Longest time for the groups', hint: 'The last group starts by this time even if earlier ones are not done.', min: 20, max: 100, dflt: 60 },
];
export const PICKUP_KM = { min: 0.5, max: 50 };

const blank = (v) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

/** Whole number inside the field's range, or null for blank / not a number. */
export function parseField(raw, field) {
  if (blank(raw)) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.min(field.max, Math.max(field.min, Math.round(n)));
}

/** Kilometres inside 0.5-50, or null for blank / not a number. */
export function parsePickupKm(raw) {
  if (blank(raw)) return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.min(PICKUP_KM.max, Math.max(PICKUP_KM.min, n));
}

// Shadow mode: 'on' = log what the new method would have done on every search (no effect on
// anyone), 'off' = explicitly not, '' = not set here (global: off; town: same as all towns).
const SHADOW_VALUES = ['on', 'off'];

const emptyLayer = (strategy = '') => ({
  strategy,
  shadowV2: '',
  ...Object.fromEntries(FIELDS.map((f) => [f.key, ''])),
  maxPickupKm: '',
});

function layerFromStored(src, strategyDefault) {
  const base = emptyLayer(strategyDefault);
  if (!src || typeof src !== 'object') return base;
  FIELDS.forEach((f) => { if (!blank(src[f.key])) base[f.key] = String(src[f.key]); });
  if (!blank(src.maxPickupKm)) base.maxPickupKm = String(src.maxPickupKm);
  if (STRATEGIES.includes(src.strategy)) base.strategy = src.strategy;
  if (src.shadowV2 === true) base.shadowV2 = 'on';
  else if (src.shadowV2 === false) base.shadowV2 = 'off';
  return base;
}

/** Form state (all strings, blank = default / inherit) from the stored `dispatchV2` field. */
export function formFromStored(stored) {
  const root = stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
  const towns = {};
  const storedTowns = root.towns && typeof root.towns === 'object' ? root.towns : {};
  Object.keys(storedTowns).forEach((id) => { towns[id] = layerFromStored(storedTowns[id], ''); });
  return { global: layerFromStored(root, 'rings'), towns };
}

function layerPayload(layer, { isGlobal }) {
  const out = {};
  if (isGlobal) out.strategy = STRATEGIES.includes(layer.strategy) ? layer.strategy : 'rings';
  else if (STRATEGIES.includes(layer.strategy)) out.strategy = layer.strategy;
  FIELDS.forEach((f) => {
    const v = parseField(layer[f.key], f);
    if (v !== null) out[f.key] = v;
  });
  const km = parsePickupKm(layer.maxPickupKm);
  if (km !== null) out.maxPickupKm = km;
  // The platform layer only ever stores `true` (absent = off); a town may also say `false`.
  if (layer.shadowV2 === 'on') out.shadowV2 = true;
  else if (layer.shadowV2 === 'off' && !isGlobal) out.shadowV2 = false;
  return out;
}

/**
 * The object to save as `dispatchV2`: the platform layer (always has a strategy) plus a `towns` map
 * holding ONLY the towns that actually override something. Blank boxes are left out, never saved
 * as empty text, so "blank" always means "use the default / the platform value".
 */
export function buildDispatchV2(form) {
  const payload = layerPayload(form?.global || emptyLayer('rings'), { isGlobal: true });
  const towns = {};
  Object.keys(form?.towns || {}).forEach((id) => {
    const t = layerPayload(form.towns[id], { isGlobal: false });
    if (Object.keys(t).length) towns[id] = t;
  });
  if (Object.keys(towns).length) payload.towns = towns;
  return payload;
}

/**
 * What the server will use for one town: { strategy, values: {batchSize, ..., maxPickupKm} }.
 * `zoneRadiusKm` is the town's own radius, used when no pickup limit is set.
 */
export function effectiveForTown(stored, townId, zoneRadiusKm) {
  const root = stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
  const townKey = townId == null ? 'default' : String(townId);
  const towns = root.towns && typeof root.towns === 'object' ? root.towns : {};
  const town = towns[townKey] && typeof towns[townKey] === 'object' ? towns[townKey] : {};
  const pick = (key) => (!blank(town[key]) ? town[key] : root[key]);
  const values = {};
  FIELDS.forEach((f) => {
    const v = parseField(pick(f.key), f);
    values[f.key] = v === null ? f.dflt : v;
  });
  const km = parsePickupKm(pick('maxPickupKm'));
  const radius = Number(zoneRadiusKm);
  values.maxPickupKm = km !== null ? km : (Number.isFinite(radius) && radius > 0 ? radius : null);
  const strategyRaw = pick('strategy');
  return {
    strategy: STRATEGIES.includes(strategyRaw) ? strategyRaw : 'rings',
    shadow: pick('shadowV2') === true,
    values,
  };
}

/** One row of the "recent searches" table, from the fields the server pins on every ride. */
export function summarizeRideDispatch(ride) {
  const plan = ride && ride.dispatchPlan && typeof ride.dispatchPlan === 'object' ? ride.dispatchPlan : null;
  const used = ride && typeof ride.dispatchStrategy === 'string' ? ride.dispatchStrategy : null;
  const asked = ride && typeof ride.dispatchStrategyRequested === 'string' ? ride.dispatchStrategyRequested : used;
  const maxM = plan && Number.isFinite(Number(plan.maxPickupM)) ? Number(plan.maxPickupM) : null;
  return {
    town: (ride && ride.zoneId) || 'default',
    pinned: !!used,
    used,
    asked,
    perGroup: plan && Number.isFinite(Number(plan.batchSize)) ? Number(plan.batchSize) : null,
    groupSeconds: plan && Number.isFinite(Number(plan.waveHoldSec)) ? Number(plan.waveHoldSec) : null,
    maxPickupKm: maxM == null ? null : Math.round((maxM / 1000) * 10) / 10,
  };
}

export const methodLabel = (s) => (s === 'nearest' ? 'Nearest few first' : s === 'batched' ? 'Circles in batches' : s === 'rings' ? 'Circles (today)' : '—');
